import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Skeleton,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Percent as PercentIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../api/client';
import { formatCurrency } from '../../utils/currency';
import DevOnly from '../../components/DevOnly';

const DiscountsManagement = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch discounts
  const { data: discountsData, isLoading, error } = useQuery({
    queryKey: ['discounts', page, rowsPerPage, searchQuery],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/financial/discounts', {
          params: {
            skip: page * rowsPerPage,
            limit: rowsPerPage,
            search: searchQuery || undefined
          }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching discounts:', error);
        return { discounts: [], total: 0, stats: {} };
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  const discounts = discountsData?.discounts || [];
  const total = discountsData?.total || 0;
  const stats = discountsData?.stats || {};

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const formatDiscountRate = (type, rate) => {
    const absRate = Math.abs(rate);
    return type === '1' ? `${absRate}%` : formatCurrency(absRate);
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load discounts. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Discounts Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage discount codes and promotional offers
          </Typography>
        </Box>
        <DevOnly>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/discounts/create')}
            size="large"
          >
            Create Discount
          </Button>
        </DevOnly>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PercentIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography color="text.secondary" variant="body2">
                  Total Discounts
                </Typography>
              </Box>
              <Typography variant="h4">
                {isLoading ? <Skeleton width={60} /> : total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ActiveIcon sx={{ mr: 1, color: 'success.main' }} />
                <Typography color="text.secondary" variant="body2">
                  Active Discounts
                </Typography>
              </Box>
              <Typography variant="h4">
                {isLoading ? <Skeleton width={60} /> : stats.active || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PercentIcon sx={{ mr: 1, color: 'info.main' }} />
                <Typography color="text.secondary" variant="body2">
                  Percentage Type
                </Typography>
              </Box>
              <Typography variant="h4">
                {isLoading ? <Skeleton width={60} /> : stats.percentage || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MoneyIcon sx={{ mr: 1, color: 'warning.main' }} />
                <Typography color="text.secondary" variant="body2">
                  Fixed Amount Type
                </Typography>
              </Box>
              <Typography variant="h4">
                {isLoading ? <Skeleton width={60} /> : stats.fixed || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search discounts by name..."
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Discounts Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Discount Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Rate</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Availability</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    {Array.from({ length: 8 }).map((_, cellIndex) => (
                      <TableCell key={`skeleton-cell-${cellIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : discounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body1" sx={{ py: 3 }}>
                      No discounts found. Create your first discount to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                discounts.map((discount) => (
                  <TableRow
                    key={discount.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/discounts/${discount.id}`)}
                  >
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {discount.name || 'Unnamed Discount'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={discount.type === '1' ? 'Percentage' : 'Fixed Amount'}
                        size="small"
                        variant="outlined"
                        icon={discount.type === '1' ? <PercentIcon /> : <MoneyIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {formatDiscountRate(discount.type, discount.rate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={discount.is_active ? 'Active' : 'Inactive'}
                        color={discount.is_active ? 'success' : 'default'}
                        size="small"
                        icon={discount.is_active ? <ActiveIcon /> : <InactiveIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={discount.online ? 'Online' : 'In-Store'}
                        color={discount.online ? 'primary' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {discount.description || 'No description'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {discount.created_date ? 
                        new Date(discount.created_date).toLocaleDateString() : 
                        'N/A'
                      }
                    </TableCell>
                    <TableCell align="center">
                      <DevOnly>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/discounts/${discount.id}/edit`);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Handle delete
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </DevOnly>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {discounts.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </Box>
  );
};

export default DiscountsManagement;